import { Injectable } from '@angular/core'
import { GameName, GameNames } from '@services/lobby.service'
import { MediaService } from '@services/media.service'
import { ResultService } from '@services/result.service'
import { format } from 'date-fns'
import { BehaviorSubject, combineLatest, defer, Observable, Subject, Subscription, timer } from 'rxjs'
import { filter, map, share, takeUntil, takeWhile, tap, withLatestFrom } from 'rxjs/operators'
import { LocaleRouterService } from './locale-router.service'

export const timeSpent = 'time_spent'
export const maxTime = 'max_time'

export type TimerKey = 'game' | 'total' | 'limit' | GameName

export type GameTime = {
  [index in GameName]?: number
}

export const INITIAL = 300;
export const MAX = 1800;

@Injectable({
  providedIn: 'root'
})
export class TimerService {
  ending: boolean = false;

  timers: Timers = new Timers();

  totalSubscription?: Subscription;
  limitSubscription?: Subscription;
  activeSubscription?: Subscription;

  constructor(
    public result: ResultService,
    private media: MediaService,
    private router: LocaleRouterService) {
    this.init()
  }

  init() {
    const { timers } = this;

    timers.setMax(INITIAL);

    timers.init();

    this.subscribe(timers)
  }

  subscribe(timers: Timers) {
    this.totalSubscription = timers.total.timer$
      .pipe(
        map(milliseconds => milliseconds / 1000),
        tap(seconds => {
          const { max } = timers.total
          timers.total.calculatePercent()
          this.detectEnd(seconds, max)
        })
      )
      .subscribe();

    this.limitSubscription = timers.limit.timer$
      .pipe(
        map(milliseconds => milliseconds / 1000),
        tap(seconds => {
          const { max } = timers.limit;
          timers.total.calculatePercent();
          this.detectEnd(seconds, max);
        })
      )
      .subscribe(() => timers.limit.done ? this.showResult() : void (0));

    this.activeSubscription = combineLatest([
      timers.total.active$,
      timers.limit.active$])
      .subscribe(([totalActive, limitActive]) => {
        !totalActive || !limitActive ? this.media.stop('clock') : void (0);
      });
  }

  detectEnd(seconds: number, max: number) {
    if (seconds >= max - 10) {
      if (!this.ending) {
        this.media.play('clock');
        this.ending = true;
      }
      this.media.vibrate([100]);
    }
    if (seconds === max) {
      this.media.vibrate([1000]);
      this.media.stop('clock');
      this.ending = false;
    }
  }

  showResult() {
    this.result
      .back()
      .then(() => {
        this.result.show()
          .subscribe(another => {
            another
              ? this.another()
              : this.continue()
          })
      });
  }

  another() {
    const total = this.timers.currentTimeAndReset()
    this.router.games({ ...total })
      .then(() => {
        this.result.visible$.next(false)
      })
  }

  leave() {
    const total = this.timers.currentTimeAndReset()
    this.router.exit({ ...total })
      .then(() => {
        this.result.visible$.next(false);
        this.totalSubscription?.unsubscribe();
        this.limitSubscription?.unsubscribe();
        this.activeSubscription?.unsubscribe();
        this.timers.unsubscribeAll();
        this.init();
      })
  }

  continue() {

  }
}

class Ticker {
  ticker$: Observable<void>;

  constructor(private fps: number = 1) {
    this.ticker$ = timer(0, 1000 / fps)
      .pipe(
        map(() => void (0)),
        share(),
      );
  }

  getTimer(
    active: Observable<boolean>,
    initial: number,
  ): Observable<number> {
    return defer(() => {
      let seconds = initial || 0;
      return this.ticker$.pipe(
        withLatestFrom(active),
        filter(([_, active]) => active),
        map(() => seconds += (1 / this.fps)),
      );
    });
  }
}

class Timer {
  private static readonly ticker = new Ticker();

  active$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  stopWatch$: BehaviorSubject<string> = new BehaviorSubject<string>('0');

  timer$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  end$: Subject<void> = new Subject<void>()

  max: number = 0
  done: boolean = false
  timeFormat: string

  constructor(public readonly key: TimerKey) {
    if (!key) {
      throw new Error('Key required for Timer');
    }
    this.timeFormat = key === 'total' ? 'mm:ss' : 'm';
  }

  get current() {
    return this.load(timeSpent)
  }

  init() {
    const { key } = this
    const max: number = key === 'total'
      ? this.load(maxTime, false)
      : MAX

    const initialTime = this.getInitialTime();
    this.stopWatch$.next(format(initialTime * 1000, this.timeFormat));

    Timer.ticker.getTimer(this.active$, initialTime)
      .pipe(
        takeWhile(seconds => seconds <= max),
        takeUntil(this.end$.pipe(tap(() => console.log("end!")))),
        tap(seconds => this.saveTime(seconds)),
        map(seconds => seconds * 1000))
      .subscribe({
        next: milliseconds => {
          this.stopWatch$.next(format(milliseconds, this.timeFormat));
          this.timer$.next(milliseconds);
        },
        complete: () => {
          this.done = true;
          this.stopWatch$.next(this.stopWatch$.value);
          this.timer$.next(this.timer$.value);
        }
      })

    return this
  }

  private getInitialTime(): number {
    const { current } = this
    return current && current > 0 ? current : 0
  }

  protected saveTime(seconds: number | string) {
    if (seconds) {
      localStorage.setItem(`${this.key}-${timeSpent}`, `${seconds}`);
    } else {
      localStorage.removeItem(`${this.key}-${timeSpent}`);
    }
  }

  protected load(key: string, prefix: boolean = true) {
    const local = localStorage.getItem(prefix ? `${this.key}-${key}` : key)
    return local ? +local : 0
  }

  private clear(key: string) {
    localStorage.removeItem(`${this.key}-${key}`)
  }

  start() {
    this.active$.next(true)
    return this
  }

  stop() {
    this.active$.next(false)
    return this
  }

  reset() {
    this.end$.next();
    this.stop();
    this.saveTime(0);
    this.stopWatch$.next(format(0, this.timeFormat));
    this.timer$.next(0);
    this.done = false;
    this.init();
    return this;
  }

  unsubscribe() {
    this.end$.next();
    this.stop();
    this.saveTime(0);
    this.stopWatch$.next(format(0, this.timeFormat));
    this.timer$.next(0);
    this.done = false;
    return this;
  }
}

class TotalTimer extends Timer {
  percent: number = 0

  constructor() {
    super('total')
  }

  calculatePercent() {
    const max = Math.floor(this.load(maxTime, false) / 60);
    const spent = Math.floor(this.load(timeSpent) / 60);
    const percent = spent / max * 100 | 0;
    return this.percent = Math.max(0, Math.min(100, percent));
  }
}

class LimitTimer extends Timer {
  max: number = MAX

  constructor() {
    super('limit')
  }
}

export class Timers {
  private static readonly Names: TimerKey[] = [
    // Exercises
    'tension',
    // Games
    'memoryCards',
    'findNumber',
    'fifteen',
    'mascot',
    'quiz',
    // Meditations
    'waves',
    'hole',
    'particles'
  ];

  private readonly stack: Timer[] = Timers.Names.map(key => new Timer(key));
  readonly total: TotalTimer = new TotalTimer();
  readonly limit: LimitTimer = new LimitTimer();

  constructor() {
  }

  init() {
    this.stack.forEach(timer => timer.init());
    this.total.init();
    this.limit.init();
  }

  private getOrCreate(key: TimerKey): Timer {
    let timer = this.stack.find(timer => timer.key === key);
    if (!timer) {
      timer = new Timer(key);
      timer.init();
      this.stack.push(timer);
    }
    return timer;
  }

  private getOrCreateOrDefault(key?: TimerKey): Timer | undefined {
    return key == undefined
      ? undefined
      : this.getOrCreate(key);
  }

  setMax(max?: number | string) {
    max ??= INITIAL;
    this.total.max = +max;
    localStorage.setItem(maxTime, `${max}`);
    this.setLimit();
    return this;
  }

  setLimit() {
    const { limit } = this;
    limit.max = MAX;
    localStorage.setItem(`${limit.key}-${maxTime}`, `${MAX}`);
  }

  unsubscribeAll() {
    this.stack.forEach(timer => timer.unsubscribe());
    this.total.unsubscribe();
    this.limit.unsubscribe();
    return this;
  }

  start(key?: TimerKey) {
    this.getOrCreateOrDefault(key)?.start();
    this.total.start();
    this.limit.start();
    return this;
  }

  stop(key?: TimerKey) {
    this.getOrCreateOrDefault(key)?.stop();
    this.total.stop();
    this.limit.stop();
    return this;
  }

  currentTimeAndReset(): GameTime {
    const times: GameTime = {}
    this.stack.forEach(timer => {
      const key = timer.key as GameName;
      if (GameNames.includes(key) && timer.current) {
        times[key] = timer.current;
        timer.reset();
      }
    })
    return times;
  }
}
